import React, { useEffect, useRef, useState } from 'react'
import 'react-date-range/dist/styles.css'
import 'react-date-range/dist/theme/default.css'
import styled from 'styled-components'
import moment from 'moment'
import { Calendar } from 'react-date-range'
import { $DCprimaryColor } from '../../styleGuide'
import { Popover } from 'react-bootstrap'
import * as locales from 'react-date-range/dist/locale'
import PixelFlexBox from '../../pixel-flex-box/pixel-flex-box'
import { parseISO } from 'date-fns'
import { colors } from '../../styleGuide'

const DCDatepicker = (props) => {
  const [showPopOver, setShowPopOver] = useState(false)
  const [newDate, setNewDate] = useState(
    props.value ? parseISO(props.value) : new Date() // Convert props.value to Date
  )

  const datePickerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleDocumentClick = (event: MouseEvent) => {
      if (
        datePickerRef.current &&
        !datePickerRef.current.contains(event.target as Node)
      ) {
        setShowPopOver(false)
      }
    }

    document.addEventListener('mousedown', handleDocumentClick)

    return () => {
      document.removeEventListener('mousedown', handleDocumentClick)
    }
  }, [])

  return (
    <PixelFlexBox
      style={{ marginBottom: '40px' }}
      justifyContent='flex-end'
      ref={datePickerRef}
    >
      <StyledDatePicker
        onClick={() => {
          setShowPopOver(!showPopOver)
        }}
      >
        {props.value
          ? moment(props.value).format('YYYY-MM-DD ')
          : moment().format('YYYY-MM-DD')}
      </StyledDatePicker>

      {showPopOver && (
        <React.Fragment>
          <StyledPopOver>
            <Calendar
              color={$DCprimaryColor}
              onChange={(item) => {
                props.onChange(item) // Pass the new date to parent

                // setNewDate(new Date(item)) // Update state with a proper Date object
                setShowPopOver(false)
              }}
              locale={locales[{ enUS: 'English (United States)' }]}
              date={newDate}
            />
          </StyledPopOver>
        </React.Fragment>
      )}
    </PixelFlexBox>
  )
}

const StyledDatePicker = styled.div`
  appearance: none;
  background-clip: padding-box;
  background-color: ${colors.white};
  border: 0.0625rem solid ${colors.grayBorder};
  border-radius: 0.375rem;
  color: ${colors.grayInputDark};
  display: block;
  font-size: 0.875rem;
  font-weight: 400;
  line-height: 1.5rem;
  padding: 0.5625rem 1.125rem;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
  width: 100%;
  cursor: pointer;
  gap: 10px;
  color: ${colors.grayMedium};
  display: flex;
  justify-content: flex-start;
  align-items: center;
  position: absolute;
`

const StyledPopOver = styled(Popover)`
  max-width: 500px;
  border: none;
  box-shadow: -3px 4px 11px 2px rgba(169, 169, 169, 0.8);
  top: 50px;
  display: block;
  .popover-arrow {
    display: none;
  }
  position: absolute;
`

const DatepickerContainer = styled.div<{ isShowError: boolean }>`
  position: relative;
  width: 100%;

  .error-message {
    color: ${colors.danger};
    font-size: 12px;
    margin-top: 4px;
    display: ${(props) => (props.isShowError ? 'block' : 'none')};
  }

  .error-icon {
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
    color: ${colors.danger};
    font-size: 16px;
  }

  .error-border {
    border-top: 5px solid ${colors.danger};
  }
`

const StyledDatepicker = styled.input<{ isShowError: boolean }>`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid ${(props) => (props.isShowError ? colors.danger : colors.grayBorder)};
  border-radius: 4px;
  font-size: 14px;
  transition: all 0.2s ease-in-out;
  background-color: ${colors.white};
  color: ${colors.grayInputDark};

  &:focus {
    outline: none;
    border-color: ${(props) => (props.isShowError ? colors.danger : colors.primary)};
    box-shadow: 0 0 0 2px ${(props) => (props.isShowError ? colors.dangerLight : colors.primaryLight)};
  }

  &:disabled {
    background-color: ${colors.grayLighter} !important;
    border-color: ${colors.grayBorder} !important;
    color: ${colors.grayMedium};
    cursor: not-allowed;
  }

  &::placeholder {
    color: ${colors.grayMedium};
  }

  &.error {
    background-color: ${colors.dangerLight} !important;
    border-color: ${colors.danger} !important;
  }

  &.success {
    border: 0.0625rem solid ${(props) => (props.isShowError ? colors.danger : colors.grayBorder)};
    color: ${colors.grayInputDark};
  }

  &.warning {
    border-color: ${colors.warning};
  }

  &.info {
    border-color: ${colors.info};
  }

  &.default {
    border: 0.0625rem solid ${(props) => (props.isShowError ? colors.danger : colors.grayBorder)};
    color: ${colors.grayInputDark};
  }

  &.outlined {
    border: 2px solid ${(props) => (props.isShowError ? colors.danger : colors.grayBorder)};
    background-color: transparent;
  }

  &.filled {
    border: none;
    border-bottom: 2px solid ${(props) => (props.isShowError ? colors.danger : colors.grayBorder)};
    background-color: ${(props) => (props.isShowError ? colors.dangerLight : colors.white)};
    border-radius: 0;
  }

  &.standard {
    border: none;
    border-bottom: 1px solid ${(props) => (props.isShowError ? colors.danger : colors.grayBorder)};
    background-color: transparent;
    border-radius: 0;
  }

  &.small {
    padding: 6px 8px;
    font-size: 12px;
  }

  &.medium {
    padding: 10px 12px;
    font-size: 14px;
  }

  &.large {
    padding: 14px 16px;
    font-size: 16px;
  }

  &.primary {
    border-color: ${colors.primary};
  }

  &.secondary {
    border-color: ${colors.secondary};
  }

  &.success {
    border-color: ${colors.success};
  }

  &.error {
    border-color: ${colors.danger};
  }

  &.warning {
    border-color: ${colors.warning};
  }

  &.info {
    border-color: ${colors.info};
  }

  &.full-width {
    width: 100%;
  }
`

export default DCDatepicker
